<script >
import ChartingFundamentalMiniChart from '@/features/charting-next/components/workspace/sidebar/ChartingFundamentalMiniChart.vue';
import { computed, ref } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  resource: {
    type: Object,
    default: () => ({})
  },
  hasSelectedTicker: {
    type: Boolean,
    default: true
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const METRIC_DEFINITIONS = {
    sharesOutstanding: 'Total de acciones emitidas por la compania que existen actualmente.',
    sharesFloat: 'Acciones disponibles para negociarse en mercado, excluyendo participaciones restringidas.',
    percentInsiders: 'Porcentaje aproximado de acciones en manos de directivos o personas vinculadas a la compania.',
    percentInstitutions: 'Porcentaje aproximado de acciones en manos de fondos, gestoras u otras instituciones.',
    bookValue: 'Valor contable por accion segun el balance publicado.',
    pegRatio: 'Relacion entre PER y crecimiento esperado. Ayuda a poner el PER en contexto.',
    peRatio: 'Precio de la accion dividido por el beneficio por accion. Mide cuanto paga el mercado por cada unidad de beneficio.',
    enterpriseValue: 'Valor total estimado de la empresa, incluyendo deuda y caja.',
    enterpriseValueEbitda: 'Valor de empresa dividido por EBITDA. Compara valor de empresa frente a beneficio operativo.',
    enterpriseValueRevenue: 'Valor de empresa dividido por ingresos. Compara valor de empresa frente a ventas.',
    totalEsg: 'Puntuacion agregada de criterios ambientales, sociales y de gobierno corporativo.',
    environmentScore: 'Puntuacion de la dimension ambiental.',
    socialScore: 'Puntuacion de la dimension social.',
    governanceScore: 'Puntuacion de gobierno corporativo.',
    controversyLevel: 'Nivel de controversias registradas en materia ESG.'
  };
  const CHART_DEFINITIONS = {
    balance: 'Evolucion historica de caja o balance disponible segun el proveedor de datos.',
    cashFlow: 'Evolucion historica del flujo de caja al cierre de cada periodo.',
    incomeStatement: 'Evolucion historica de los ingresos o ventas totales.',
    earnings: 'Beneficio por accion anual publicado.',
    outstandingShares: 'Evolucion historica del numero de acciones en mercado.'
  };
  const SECTION_DEFINITIONS = {
    holders: ['Principales instituciones que declaran posiciones sobre la compania.', 'Acciones actuales muestra el tamano aproximado de esa posicion.'],
    officers: ['Listado de ejecutivos incluidos en la ficha fundamental del proveedor.', 'Cargo indica la responsabilidad principal reportada.'],
    activities: ['Actividades sensibles o controvertidas detectadas por el proveedor ESG.', 'Involucramiento indica el nivel de exposicion informado.']
  };
  const props = __props;
  const infoPopover = ref(null);
  const activeInfo = ref({
    title: '',
    items: []
  });
  const fundamental = computed(() => props.resource.data?.value || null);
  const metricSections = computed(() => fundamental.value?.metricSections || []);
  const chartSections = computed(() => fundamental.value?.chartSections || []);
  const holders = computed(() => fundamental.value?.institutionHolders || []);
  const officers = computed(() => fundamental.value?.officers || []);
  const activities = computed(() => fundamental.value?.activities || []);
  const showLoadingState = computed(() => props.hasSelectedTicker && !props.resource.error?.value && (props.resource.isLoading?.value || !props.resource.isReady?.value && !fundamental.value));
  const buildMetricInfoItems = section => (section?.metrics || []).filter(metric => metric.isAvailable).map(metric => ({
    label: metric.label,
    description: METRIC_DEFINITIONS[metric.id] || 'Metrica fundamental publicada por el proveedor de datos.'
  }));
  const buildChartInfoItems = section => (section?.charts || []).map(chart => ({
    label: chart.title,
    description: CHART_DEFINITIONS[chart.id] || 'Serie historica publicada por el proveedor de datos.'
  }));
  const buildStaticInfoItems = (items = []) => items.map((description, index) => ({
    label: index === 0 ? 'Que muestra' : 'Como leerlo',
    description
  }));
  const hasChartSectionInfo = section => section?.id !== 'earnings';
  const toggleInfo = (event, title, items = []) => {
    activeInfo.value = {
      title,
      items
    };
    infoPopover.value?.toggle(event);
  };
  return {
    SECTION_DEFINITIONS,
    infoPopover,
    activeInfo,
    fundamental,
    metricSections,
    chartSections,
    holders,
    officers,
    activities,
    showLoadingState,
    buildMetricInfoItems,
    buildChartInfoItems,
    buildStaticInfoItems,
    hasChartSectionInfo,
    toggleInfo
  };
};
__sfc_main.components = Object.assign({
  ChartingFundamentalMiniChart
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <div class="charting-fundamental-panel">
        <div v-if="showLoadingState" class="charting-workspace-inspector__loader charting-fundamental-loading">
            <ProgressSpinner style="width: 32px; height: 32px" stroke-width="6" />
            <span>Cargando análisis fundamental...</span>
        </div>
        <div v-else-if="resource.error && resource.error.value" class="brand-banner-warning rounded-xl border p-4 text-sm">{{ resource.error.value.message || 'No se pudo cargar el análisis fundamental.' }}</div>
        <div v-else-if="!hasSelectedTicker" class="brand-empty-state rounded-xl border border-dashed p-4 text-sm">Selecciona un ticker para ver su análisis fundamental.</div>
        <template v-else-if="fundamental && fundamental.hasData">
            <section v-for="section in metricSections" :key="section.id" class="charting-fundamental-section">
                <div class="charting-fundamental-section__header">
                    <h3>{{ section.title }}</h3>
                    <Button icon="pi pi-info-circle" text rounded size="small" severity="secondary" :aria-label="`Informacion de ${section.title}`" @click="toggleInfo($event, section.title, buildMetricInfoItems(section))" />
                </div>
                <div class="charting-fundamental-metric-grid">
                    <div v-for="metric in section.metrics" :key="metric.id" class="charting-fundamental-metric">
                        <div class="charting-fundamental-metric__label">{{ metric.label }}</div>
                        <div class="charting-fundamental-metric__value" :title="metric.fullValue || metric.displayValue">{{ metric.displayValue }}</div>
                    </div>
                </div>
            </section>

            <section v-for="section in chartSections" :key="section.id" class="charting-fundamental-section">
                <div class="charting-fundamental-section__header">
                    <h3>{{ section.title }}</h3>
                    <Button
                        v-if="hasChartSectionInfo(section)"
                        icon="pi pi-info-circle"
                        text
                        rounded
                        size="small"
                        severity="secondary"
                        :aria-label="`Informacion de ${section.title}`"
                        @click="toggleInfo($event, section.title, buildChartInfoItems(section))"
                    />
                </div>
                <div class="charting-fundamental-chart-grid">
                    <ChartingFundamentalMiniChart v-for="chart in section.charts" :key="chart.id" :chart="chart" />
                </div>
            </section>

            <section v-if="holders.length" class="charting-fundamental-section">
                <div class="charting-fundamental-section__header">
                    <h3>Instituciones</h3>
                    <Button icon="pi pi-info-circle" text rounded size="small" severity="secondary" aria-label="Informacion de Instituciones" @click="toggleInfo($event, 'Instituciones', buildStaticInfoItems(SECTION_DEFINITIONS.holders))" />
                </div>
                <div class="charting-fundamental-table">
                    <div class="charting-fundamental-table__row charting-fundamental-table__row--head">
                        <span>Instituciones</span>
                        <span>Acciones actuales</span>
                    </div>
                    <div v-for="holder in holders" :key="holder.id" class="charting-fundamental-table__row">
                        <span>{{ holder.institution }}</span>
                        <span :title="holder.currentSharesFullLabel">{{ holder.currentSharesLabel }}</span>
                    </div>
                </div>
            </section>

            <section v-if="officers.length" class="charting-fundamental-section">
                <div class="charting-fundamental-section__header">
                    <h3>Ejecutivos</h3>
                </div>
                <div class="charting-fundamental-list">
                    <div v-for="officer in officers" :key="officer.id" class="charting-fundamental-list__item">
                        <span>{{ officer.name }}</span>
                        <strong>{{ officer.title }}</strong>
                    </div>
                </div>
            </section>

            <section v-if="activities.length" class="charting-fundamental-section">
                <div class="charting-fundamental-section__header">
                    <h3>Participacion en actividades</h3>
                    <Button
                        icon="pi pi-info-circle"
                        text
                        rounded
                        size="small"
                        severity="secondary"
                        aria-label="Informacion de participacion en actividades"
                        @click="toggleInfo($event, 'Participacion en actividades', buildStaticInfoItems(SECTION_DEFINITIONS.activities))"
                    />
                </div>
                <div class="charting-fundamental-table">
                    <div class="charting-fundamental-table__row charting-fundamental-table__row--head">
                        <span>Actividad</span>
                        <span>Involucramiento</span>
                    </div>
                    <div v-for="activity in activities" :key="activity.id" class="charting-fundamental-table__row">
                        <span>{{ activity.label }}</span>
                        <span>{{ activity.involvement }}</span>
                    </div>
                </div>
            </section>
        </template>
        <div v-else class="brand-empty-state rounded-xl border border-dashed p-4 text-sm">Actualmente no hay análisis fundamental disponible para este ticker.</div>

        <Popover ref="infoPopover" append-to="body">
            <div class="charting-fundamental-info-popover">
                <div class="charting-fundamental-info-popover__title">{{ activeInfo.title }}</div>
                <div class="charting-fundamental-info-popover__list">
                    <div v-for="item in activeInfo.items" :key="`${activeInfo.title}-${item.label}`" class="charting-fundamental-info-popover__item">
                        <strong>{{ item.label }}</strong>
                        <span>{{ item.description }}</span>
                    </div>
                </div>
            </div>
        </Popover>
    </div>
</template>

<style scoped>
.charting-fundamental-panel {
    display: flex;
    min-height: 0;
    flex-direction: column;
    gap: 1rem;
}

.charting-fundamental-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.charting-fundamental-section__header {
    display: flex;
    height: 1.75rem;
    align-items: center;
    justify-content: flex-start;
    gap: 0.35rem;
}

.charting-fundamental-section__header ::v-deep .p-button {
    width: 1.75rem;
    min-width: 1.75rem;
    height: 1.75rem;
    padding: 0;
}

.charting-fundamental-section__header h3 {
    margin: 0;
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    line-height: 1.1;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.charting-fundamental-loading {
    min-height: 9rem;
    align-items: center;
    flex-direction: column;
    gap: 0.75rem;
    color: var(--p-text-muted-color);
    font-size: 0.86rem;
}

.charting-fundamental-metric-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.charting-fundamental-metric {
    min-width: 0;
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    border-radius: 1rem;
    padding: 0.85rem 1rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
}

.charting-fundamental-metric__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.charting-fundamental-metric__value {
    margin-top: 0.4rem;
    overflow-wrap: anywhere;
    font-weight: 600;
    color: var(--p-text-color);
}

.charting-fundamental-chart-grid {
    display: grid;
    gap: 0.7rem;
}

.charting-fundamental-table,
.charting-fundamental-list {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
}

.charting-fundamental-table__row {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
    align-items: start;
    gap: 0.75rem;
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    border-radius: 1rem;
    padding: 0.85rem 1rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
    font-size: 0.78rem;
    color: var(--p-text-color);
}

.charting-fundamental-list {
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    border-radius: 1rem;
    padding: 0.3rem 1rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
}

.charting-fundamental-list__item {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
    align-items: start;
    gap: 0.75rem;
    border-bottom: 1px solid color-mix(in srgb, var(--charting-b-border-color, rgba(148, 163, 184, 0.18)) 70%, transparent);
    padding: 0.55rem 0;
    font-size: 0.78rem;
    color: var(--p-text-color);
}

.charting-fundamental-list__item:last-child {
    border-bottom: 0;
}

.charting-fundamental-table__row span,
.charting-fundamental-list__item span,
.charting-fundamental-list__item strong {
    min-width: 0;
    overflow-wrap: anywhere;
}

.charting-fundamental-table__row span:last-child,
.charting-fundamental-list__item strong {
    text-align: right;
}

.charting-fundamental-table__row--head {
    border: 0;
    border-radius: 0;
    padding: 0 1rem;
    background: transparent;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.charting-fundamental-info-popover {
    width: min(22rem, calc(100vw - 2rem));
    color: var(--p-text-color);
}

.charting-fundamental-info-popover__title {
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
    font-weight: 800;
}

.charting-fundamental-info-popover__list {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
}

.charting-fundamental-info-popover__item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    font-size: 0.8rem;
    line-height: 1.35;
}

.charting-fundamental-info-popover__item strong {
    font-size: 0.76rem;
}

.charting-fundamental-info-popover__item span {
    color: var(--p-text-muted-color);
}

@media (max-width: 640px) {
    .charting-fundamental-metric-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .charting-fundamental-table__row,
    .charting-fundamental-list__item {
        grid-template-columns: minmax(0, 1fr);
    }

    .charting-fundamental-table__row span:last-child,
    .charting-fundamental-list__item strong {
        text-align: left;
    }
}
</style>
